import React from "react";
import { createPortal } from "react-dom";
import ReactDatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import "campaign-builder/ui/datepicker.css";
import { CalendarPlus } from "lucide-react";
import cn from "classnames";
import { isBusinessDay } from "campaign-builder/state";

export interface LaunchDatePickerProps {
  value: Date | null;
  onChange: (date: Date | null) => void;
  minDate?: Date;
  error?: boolean;
  placeholder?: string;
  "data-cypress-id"?: string;
  "data-cy"?: string;
  "aria-invalid"?: boolean;
  "aria-describedby"?: string;
}

interface TriggerProps {
  value?: string;
  onClick?: () => void;
  error?: boolean;
  placeholder?: string;
  "data-cypress-id"?: string;
  "data-cy"?: string;
  "aria-invalid"?: boolean;
  "aria-describedby"?: string;
}

const Trigger = React.forwardRef<HTMLButtonElement, TriggerProps>(
  (
    {
      value,
      onClick,
      error,
      placeholder,
      "data-cypress-id": dataCypressId,
      "data-cy": dataCy,
      "aria-invalid": ariaInvalid,
      "aria-describedby": ariaDescribedby,
    },
    ref
  ) => (
    <button
      type="button"
      ref={ref}
      data-cypress-id={dataCypressId}
      // The picker's own selector data-cy sits on the trigger button — it is
      // the control an agent actually clicks to open the calendar.
      data-cy={dataCy ? `${dataCy}Button` : undefined}
      onClick={onClick}
      aria-invalid={ariaInvalid}
      aria-describedby={ariaDescribedby}
      className={cn(
        "flex h-9 w-full items-center justify-between rounded-[10px] border border-solid bg-background pl-3 pr-2 text-left text-sm focus:outline-none focus-visible:border-slate-400 dark:focus-visible:border-slate-500 focus-visible:ring-1 focus-visible:ring-slate-200 dark:focus-visible:ring-slate-700",
        error ? "border-destructive" : "border-border"
      )}
    >
      <span className={value ? "text-foreground" : "text-muted-foreground"}>{value || placeholder}</span>
      <CalendarPlus className="text-foreground h-[18px] w-[18px]" strokeWidth={1.5} />
    </button>
  )
);
Trigger.displayName = "LaunchDatePickerTrigger";

const dayCyId = (date: Date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

export const LaunchDatePicker = ({
  value,
  onChange,
  minDate,
  error,
  placeholder = "Select a date to launch",
  "data-cypress-id": dataCypressId,
  "data-cy": dataCy,
  "aria-invalid": ariaInvalid,
  "aria-describedby": ariaDescribedby,
}: LaunchDatePickerProps) => (
  <ReactDatePicker
    selected={value}
    onChange={onChange}
    minDate={minDate}
    filterDate={isBusinessDay}
    dateFormat="MMM d, yyyy"
    popperPlacement="bottom-start"
    calendarClassName="cb-datepicker"
    wrapperClassName="block w-full"
    popperContainer={({ children }) => createPortal(children, document.body)}
    // Anchors each rendered day cell so a driving agent can pick an exact date
    // from the open calendar (admLaunchDateDay-<yyyy-mm-dd>) instead of only
    // ever opening/closing the trigger.
    renderDayContents={(day, date) =>
      dataCy && date ? <span data-cy={`${dataCy}Day-${dayCyId(date)}`}>{day}</span> : day
    }
    customInput={
      <Trigger
        error={error}
        placeholder={placeholder}
        data-cypress-id={dataCypressId}
        data-cy={dataCy}
        aria-invalid={ariaInvalid}
        aria-describedby={ariaDescribedby}
      />
    }
  />
);
